/**
 * OurTeamEditor · v2.7.65+E239
 * ============================
 * Website-Management editor for the public /our-team page. Manages four fixed
 * sections (Leadership · Dispatch · Drivers · Specialty Support) — add / edit /
 * remove profiles, upload a photo (≤25 MB via the shared GridFS endpoint), set
 * name, position title, short bio, phone and email, move a profile to another
 * section, and edit the dispatch phone + email shown at the bottom of the page.
 *
 * Fully AUTOSAVING (debounced PUT to /admin/site/our-team, no save button).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/admin/ImageUploader";
import adminApi from "@/lib/adminApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Check, CloudOff, Users, Phone, Mail, GripVertical } from "lucide-react";

const MAX_25 = 25 * 1024 * 1024;
const UPLOAD = "/admin/uploads/premium";

const SECTION_ORDER = ["leadership", "dispatch", "drivers", "specialty_support"];
const SECTION_LABEL = { leadership: "Leadership", dispatch: "Dispatch", drivers: "Drivers", specialty_support: "Specialty Support" };

const newProfile = () => ({
  id: `otp_${Math.random().toString(36).slice(2, 10)}`,
  name: "", title: "", bio: "", phone: "", email: "", image_url: "", enabled: true,
});

const BIO_MAX_WORDS = 150;
const countWords = (s) => ((s || "").trim().length ? (s || "").trim().split(/\s+/).length : 0);
const capWords = (s, max) => {
  const w = (s || "").trim().length ? (s || "").trim().split(/\s+/) : [];
  return w.length > max ? w.slice(0, max).join(" ") : (s || "");
};

export default function OurTeamEditor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle");
  const timer = useRef(null);

  useEffect(() => {
    api.get("/site/our-team")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load Our Team"))
      .finally(() => setLoading(false));
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, []);

  const scheduleSave = useCallback((next) => {
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await adminApi.put("/admin/site/our-team", next); setSaveState("saved"); }
      catch { setSaveState("error"); toast.error("Autosave failed — retrying on next change"); }
    }, 700);
  }, []);

  const update = useCallback((fn) => {
    setData((cur) => {
      const next = JSON.parse(JSON.stringify(cur));
      fn(next);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  // Drag-and-drop reorder state (native HTML5 DnD, handle-driven).
  const dragSrc = useRef(null); // { key, idx }
  const [dragState, setDragState] = useState({ key: null, from: null, over: null });

  if (loading || !data) {
    return <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="our-team-editor-loading"><Loader2 className="h-7 w-7 animate-spin mx-auto" /></Card>;
  }

  // Sections come back in canonical order from the API.
  const sections = data.sections || [];
  const dispatch = data.dispatch || {};
  const sectionIndex = (key) => sections.findIndex((s) => s.key === key);

  const addProfile = (key) => update((d) => {
    const i = d.sections.findIndex((s) => s.key === key);
    if (i >= 0) d.sections[i].profiles = [...(d.sections[i].profiles || []), newProfile()];
  });
  const removeProfile = (key, idx) => update((d) => {
    const i = d.sections.findIndex((s) => s.key === key);
    if (i >= 0) d.sections[i].profiles.splice(idx, 1);
  });
  const setProfileField = (key, idx, field, value) => update((d) => {
    const i = d.sections.findIndex((s) => s.key === key);
    if (i >= 0) d.sections[i].profiles[idx][field] = value;
  });
  const moveProfile = (fromKey, idx, toKey) => {
    if (fromKey === toKey) return;
    update((d) => {
      const fi = d.sections.findIndex((s) => s.key === fromKey);
      const ti = d.sections.findIndex((s) => s.key === toKey);
      if (fi < 0 || ti < 0) return;
      const [moved] = d.sections[fi].profiles.splice(idx, 1);
      d.sections[ti].profiles = [...(d.sections[ti].profiles || []), moved];
    });
    toast.success(`Moved to ${SECTION_LABEL[toKey]}`);
  };
  const moveProfileTo = (key, from, to) => update((d) => {
    const i = d.sections.findIndex((s) => s.key === key);
    if (i < 0) return;
    const arr = d.sections[i].profiles;
    if (from < 0 || from >= arr.length || to < 0 || to >= arr.length || from === to) return;
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
  });
  const onDragStart = (key, idx) => (e) => {
    dragSrc.current = { key, idx };
    setDragState({ key, from: idx, over: idx });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch (_) {}
  };
  const onDragEnterCard = (key, idx) => (e) => {
    e.preventDefault();
    const s = dragSrc.current;
    if (!s || s.key !== key) return;
    setDragState((st) => (st.over === idx ? st : { ...st, over: idx }));
  };
  const onDropCard = (key, idx) => (e) => {
    e.preventDefault();
    const s = dragSrc.current;
    dragSrc.current = null;
    setDragState({ key: null, from: null, over: null });
    if (!s || s.key !== key) return;
    moveProfileTo(key, s.idx, idx);
  };
  const onDragEndCard = () => { dragSrc.current = null; setDragState({ key: null, from: null, over: null }); };

  return (
    <div className="space-y-4 mt-3" data-testid="our-team-editor">
      <Card className="bg-zinc-900 border-amber-400/30 p-5" data-testid="our-team-editor-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-black flex items-center gap-2 text-zinc-100"><Users className="h-5 w-5 text-amber-400" />Our Team</h3>
            <p className="text-xs text-zinc-400 mt-1">Manages the public /our-team page. Four sections — Leadership, Dispatch, Drivers, Specialty Support. Autosaves — no save button.</p>
          </div>
          <SaveBadge state={saveState} />
        </div>
      </Card>

      {SECTION_ORDER.map((key) => {
        const si = sectionIndex(key);
        if (si < 0) return null;
        const section = sections[si];
        const profiles = section.profiles || [];
        return (
          <Card key={key} className="bg-zinc-950 border border-zinc-800 p-4 space-y-4" data-testid={`our-team-section-editor-${key}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-widest text-amber-300 font-black">Section · {SECTION_LABEL[key]}</div>
              <Button data-testid={`our-team-add-${key}`} variant="outline" size="sm" className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10" onClick={() => addProfile(key)}>
                <Plus className="h-4 w-4 mr-1.5" />Add Profile
              </Button>
            </div>
            <Labeled label="Section heading (shown on the page)">
              <Input data-testid={`our-team-heading-${key}`} value={section.heading || ""} onChange={(e) => update((d) => { d.sections[si].heading = e.target.value; })} className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-md" />
            </Labeled>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <ImageUploader testid={`our-team-section-${key}-banner`} label="Section banner (optional, ≤25 MB)" value={section.image_url} onChange={(url) => update((d) => { d.sections[si].image_url = url; })} endpoint={UPLOAD} maxBytes={MAX_25} height={110} />
                <p className="text-[10px] text-zinc-500 mt-1" data-testid={`our-team-section-${key}-banner-hint`}>Recommended 1600×500px (wide landscape).</p>
              </div>
              <Labeled label="Short blurb (optional)">
                <textarea data-testid={`our-team-section-${key}-blurb`} value={section.blurb || ""} onChange={(e) => update((d) => { d.sections[si].blurb = e.target.value; })} rows={3} placeholder="A short line about this group…" className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y" />
              </Labeled>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`our-team-profiles-${key}`}>
              {profiles.map((p, idx) => {
                const isOver = dragState.key === key && dragState.over === idx && dragState.from !== idx;
                const isDragging = dragState.key === key && dragState.from === idx;
                return (
                  <div
                    key={p.id}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={onDragEnterCard(key, idx)}
                    onDrop={onDropCard(key, idx)}
                    className={`rounded-xl transition-shadow ${isOver ? "ring-2 ring-amber-400" : ""}`}
                    data-testid={`our-team-profile-drop-${key}-${idx}`}
                  >
                    <Card className={`bg-zinc-900 border border-zinc-800 p-3 space-y-3 ${isDragging ? "opacity-40" : ""}`} data-testid={`our-team-profile-editor-${key}-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            draggable
                            onDragStart={onDragStart(key, idx)}
                            onDragEnd={onDragEndCard}
                            title="Drag to reorder"
                            data-testid={`our-team-profile-${key}-${idx}-drag`}
                            className="h-7 w-7 flex items-center justify-center text-zinc-500 hover:text-amber-300 cursor-grab active:cursor-grabbing"
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Profile #{idx + 1}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <label className="flex items-center gap-1.5 text-[10px] text-zinc-400 mr-1" data-testid={`our-team-profile-${key}-${idx}-enabled`}>
                            <Switch checked={p.enabled !== false} onCheckedChange={(v) => setProfileField(key, idx, "enabled", v)} />{p.enabled !== false ? "Live" : "Hidden"}
                          </label>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-rose-400" data-testid={`our-team-profile-${key}-${idx}-delete`} onClick={() => removeProfile(key, idx)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                      <div>
                        <ImageUploader testid={`our-team-profile-${key}-${idx}-photo`} label="Photo (≤25 MB)" value={p.image_url} onChange={(url) => setProfileField(key, idx, "image_url", url)} endpoint={UPLOAD} maxBytes={MAX_25} height={140} />
                        <p className="text-[10px] text-zinc-500 mt-1" data-testid={`our-team-profile-${key}-${idx}-photo-hint`}>Recommended 800×600px (4:3) — portraits crop to center.</p>
                      </div>
                      <Input data-testid={`our-team-profile-${key}-${idx}-name`} value={p.name || ""} onChange={(e) => setProfileField(key, idx, "name", e.target.value)} placeholder="Name" className="bg-zinc-950 border-zinc-700 text-zinc-100 font-bold" />
                      <Input data-testid={`our-team-profile-${key}-${idx}-title`} value={p.title || ""} onChange={(e) => setProfileField(key, idx, "title", e.target.value)} placeholder="Position title" className="bg-zinc-950 border-zinc-700 text-zinc-100" />
                      <div>
                        <textarea data-testid={`our-team-profile-${key}-${idx}-bio`} value={p.bio || ""} onChange={(e) => setProfileField(key, idx, "bio", capWords(e.target.value, BIO_MAX_WORDS))} rows={4} placeholder="Short bio (max 150 words)" className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y" />
                        <div className={`text-[10px] text-right mt-0.5 font-bold ${countWords(p.bio) >= BIO_MAX_WORDS ? "text-rose-400" : countWords(p.bio) >= BIO_MAX_WORDS - 15 ? "text-amber-400" : "text-zinc-500"}`} data-testid={`our-team-profile-${key}-${idx}-bio-count`}>
                          {countWords(p.bio)} / {BIO_MAX_WORDS} words
                        </div>
                      </div>
                      <div className="relative"><Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid={`our-team-profile-${key}-${idx}-phone`} value={p.phone || ""} onChange={(e) => setProfileField(key, idx, "phone", e.target.value)} placeholder="Phone" className="bg-zinc-950 border-zinc-700 text-zinc-100 pl-7" /></div>
                      <div className="relative"><Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid={`our-team-profile-${key}-${idx}-email`} value={p.email || ""} onChange={(e) => setProfileField(key, idx, "email", e.target.value)} placeholder="Email" className="bg-zinc-950 border-zinc-700 text-zinc-100 pl-7" /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Order</div>
                          <select
                            data-testid={`our-team-profile-${key}-${idx}-order`}
                            value={idx}
                            onChange={(e) => moveProfileTo(key, idx, Number(e.target.value))}
                            className="w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-2 text-sm text-zinc-100"
                          >
                            {profiles.map((_, n) => <option key={n} value={n}>{n + 1}</option>)}
                          </select>
                        </label>
                        <label className="block">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Section</div>
                          <select
                            data-testid={`our-team-profile-${key}-${idx}-section`}
                            value={key}
                            onChange={(e) => moveProfile(key, idx, e.target.value)}
                            className="w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-2 text-sm text-zinc-100"
                          >
                            {SECTION_ORDER.map((k) => <option key={k} value={k}>{SECTION_LABEL[k]}</option>)}
                          </select>
                        </label>
                      </div>
                    </Card>
                  </div>
                );
              })}
              {profiles.length === 0 && <div className="col-span-full text-center text-zinc-500 text-sm py-6 border border-dashed border-zinc-800 rounded-lg">No profiles in {SECTION_LABEL[key]} yet — add one above.</div>}
            </div>
          </Card>
        );
      })}

      {/* Dispatch contact — bottom of page */}
      <Card className="bg-zinc-950 border border-amber-400/25 p-4 space-y-3" data-testid="our-team-dispatch-editor">
        <div className="text-[11px] uppercase tracking-widest text-amber-300 font-black">Dispatch Contact (bottom of page)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Labeled label="Dispatch phone"><div className="relative"><Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid="our-team-dispatch-phone" value={dispatch.phone || ""} onChange={(e) => update((d) => { d.dispatch = { ...(d.dispatch || {}), phone: e.target.value }; })} placeholder="(901) 459-9448" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" /></div></Labeled>
          <Labeled label="Dispatch email"><div className="relative"><Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid="our-team-dispatch-email" value={dispatch.email || ""} onChange={(e) => update((d) => { d.dispatch = { ...(d.dispatch || {}), email: e.target.value }; })} placeholder="support@bullertaxi.com" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" /></div></Labeled>
        </div>
      </Card>
    </div>
  );
}

function SaveBadge({ state }) {
  const map = {
    idle: { cls: "text-zinc-500", node: <>Autosaves</> },
    saving: { cls: "text-amber-300", node: <><Loader2 className="h-3 w-3 animate-spin" />Saving…</> },
    saved: { cls: "text-emerald-400", node: <><Check className="h-3 w-3" />Saved</> },
    error: { cls: "text-rose-400", node: <><CloudOff className="h-3 w-3" />Save failed</> },
  };
  const s = map[state] || map.idle;
  return <span className={`text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 ${s.cls}`} data-testid="our-team-save-state">{s.node}</span>;
}

function Labeled({ label, children }) {
  return <label className="block"><div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</div>{children}</label>;
}
